import BaseUISystem from '../BaseUISystem';
import UITextComponent from '../../../Component/UI/Text';
import Game from '../../../Game';
import Renderable from '../../../Component/Renderable';
import { UIActions } from '../../../State/UI/UIActions';
import { GameActions } from '../../../State/Game/GameActions';
import { StartAnimation } from '../../Animation/AnimationHelper';
import LeaderboardHelper from '../LeaderboardHelper';
import ColorFilter from '../../../Component/ColorFilter';
import { AUDIO_UI_BUTTON_CLOSE, AUDIO_UI_BUTTON_DEFAULT } from '../../Audio';
import GameObject from '../../../GameObject/GameObject';
import { GameObjectEvent } from '../../../GameObject/GameObjectEvent';
import UIImageComponent from '../../../Component/UI/Image';
import UINineSliceComponent from '../../../Component/UI/NineSlice';
import { WORLDS } from '../../World';
import {
    applyBounceButtonAnimation,
    applyDefaultButtonAnimation,
} from '../UIHelper';
import BaseMenu from './BaseMenu';
import { ResourceDriver } from '../../../Utility/Resource/ResourceManager';

const BTN_CLOSE: string = 'main_container/close_button';
const BTN_GO_ACTIVE: string = 'main_container/go_button/active';
const BTN_GO_INACTIVE: string = 'main_container/go_button/inactive';
const BTN_PREV: string = 'prev_button';
const BTN_NEXT: string = 'next_button';
const BANNER_IMAGE: string = 'main_container/banner/banner_image';
const BANNER_TEXT: string = 'main_container/banner/banner_text';
const WORLD_SCORE: string = 'main_container/world_score';
const TXT_STARS: string = `${WORLD_SCORE}/star_container/text`;
const TXT_CHESTS: string = `${WORLD_SCORE}/chest_container/text`;
const TXT_CARDS: string = `${WORLD_SCORE}/card_container/text`;

export default class TravelMenuHandler extends BaseMenu {
    private currentWorldID: number;
    private navigationWorldID: number;

    constructor(uiSystem: BaseUISystem) {
        super('travelMenu', uiSystem);
    }

    protected onCreate() {
        this.element
            .find(`${WORLD_SCORE}/star_container`)
            .getComponent(UINineSliceComponent).tint = 0xffbb95;
        this.element
            .find(`${WORLD_SCORE}/chest_container`)
            .getComponent(UINineSliceComponent).tint = 0xffbb95;
        this.element
            .find(`${WORLD_SCORE}/card_container`)
            .getComponent(UINineSliceComponent).tint = 0xffbb95;

        // Button close.
        const btnClose = this.element.find(BTN_CLOSE);
        btnClose.on(GameObjectEvent.SELECTED, () => {
            this.system.state.dispatch(UIActions.hidePopup(this.name));
        });
        applyDefaultButtonAnimation(btnClose, AUDIO_UI_BUTTON_CLOSE);

        // Button go.
        const btnGo = this.element.find(BTN_GO_ACTIVE);
        btnGo.on(GameObjectEvent.SELECTED, () => {
            Game.state.dispatch(GameActions.loadWorld(this.navigationWorldID));
        });
        applyBounceButtonAnimation(btnGo, AUDIO_UI_BUTTON_DEFAULT);

        // Button previous world.
        const btnPrev = this.element.find(BTN_PREV);
        btnPrev.on(GameObjectEvent.SELECTED, () => {
            this.prevWorld();
            this.changeWorld('right');
        });
        applyDefaultButtonAnimation(btnPrev, AUDIO_UI_BUTTON_DEFAULT);

        // Button next world.
        const btnNext = this.element.find(BTN_NEXT);
        btnNext.on(GameObjectEvent.SELECTED, () => {
            this.nextWorld();
            this.changeWorld('left');
        });
        applyDefaultButtonAnimation(btnNext, AUDIO_UI_BUTTON_DEFAULT);
    }

    protected onShow(): void {
        this.currentWorldID = this.system.state.getState().worldState.worldNumber;
        this.navigationWorldID = this.currentWorldID;
        this.setBannerImage();
        this.setTexts();
        this.setButtonGo();
    }

    protected onShown(): void {
        this.changeWorld('none');
    }

    private prevWorld(): void {
        this.navigationWorldID--;
        if (this.navigationWorldID < 1) {
            this.navigationWorldID = WORLDS.length;
        }
    }

    private nextWorld(): void {
        this.navigationWorldID++;
        if (this.navigationWorldID > WORLDS.length) {
            this.navigationWorldID = 1;
        }
    }

    private async setBannerImage() {
        const currentWorldID = this.navigationWorldID;
        const resourceID = WORLDS[this.navigationWorldID - 1] + '_active';

        // Lazy load banners.
        Game.resource.add(
            resourceID,
            `resource/graphics/world_banners/${resourceID}.png`,
            ResourceDriver.TERTIARY
        );
        await Game.resource.wait(resourceID);

        if (currentWorldID === this.navigationWorldID) {
            if (!this.element || this.element.destroyed) return;

            const banner = this.element.find(BANNER_IMAGE);

            if (!banner) return;

            banner.getComponent(UIImageComponent).texture = resourceID;

            if (currentWorldID <= Game.state.getState().playerState.world) {
                banner.removeComponent(ColorFilter);
            } else {
                banner.addComponent(ColorFilter);
                banner.getComponent(ColorFilter).saturation = 0;
            }
        }
    }

    private setTexts(): void {
        this.element
            .find(BANNER_TEXT)
            .getComponent(UITextComponent).text = this.getTitleText();

        this.element
            .find(TXT_STARS)
            .getComponent(UITextComponent).text = this.getStarsText();

        this.element
            .find(TXT_CHESTS)
            .getComponent(UITextComponent).text = this.getChestsText();

        this.element
            .find(TXT_CARDS)
            .getComponent(UITextComponent).text = this.getCardsText();
    }

    private setButtonGo(): void {
        const btnGoActive = this.element.find(BTN_GO_ACTIVE);
        const btnGoInactive = this.element.find(BTN_GO_INACTIVE);

        if (!btnGoInactive.hasComponent(ColorFilter)) {
            btnGoInactive.addComponent(ColorFilter, { saturation: 0 });
        }

        if (btnGoActive && btnGoInactive) {
            if (this.navigationWorldID === this.currentWorldID) {
                btnGoActive.getComponent(Renderable).visible = false;
                btnGoInactive.getComponent(Renderable).visible = false;
            } else if (
                this.navigationWorldID <=
                Game.state.getState().playerState.world
            ) {
                btnGoActive.getComponent(Renderable).visible = true;
                btnGoInactive.getComponent(Renderable).visible = false;
            } else {
                btnGoActive.getComponent(Renderable).visible = false;
                btnGoInactive.getComponent(Renderable).visible = true;
            }
        }
    }

    private changeWorld(direction: 'none' | 'left' | 'right'): void {
        // Swap banner image.
        this.setBannerImage();

        // Check button state.
        this.setButtonGo();

        StartAnimation(this.element, 'TravelMenu', {
            direction: direction,
            title: this.getTitleText(),
            stars: this.getStarsText(),
            chests: this.getChestsText(),
            cards: this.getCardsText(),
        });
    }

    private getTitleText(): string {
        return Game.locale.getText(WORLDS[this.navigationWorldID - 1]);
    }

    private getStarsText(): string {
        const stars = Game.player.getWorldStars(this.navigationWorldID);
        return `${stars}/180`;
    }

    private getChestsText(): string {
        const chests = Game.player.getWorldChestsUnlocked(
            this.navigationWorldID
        );
        return `${chests}/3`;
    }

    private getCardsText(): string {
        // 0, 1, 2: Default cards.
        // 4, 5, 6: Star cards.
        const start = (this.navigationWorldID + 1) * 3;
        const cards = Game.state
            .getState()
            .playerState.backcards.slice(start, start + 3)
            .filter(x => x > 0).length;
        return `${cards}/3`;
    }
}
